<!--
Usage:
  Single-selection dropdown component with label and customizable options.
  Supports data-driven options and Angular component projection.

Required inputs:
  label: string - Label text for the select component

Optional inputs:
  placeholder: string - Text shown when no option is selected (default: 'Select an option')
  labelPosition: 'top' | 'left' | 'right' | 'hide' - Position of label relative to select (default: 'top')
  size: 'default' | 'compact' - Size variant of the component (default: 'default')
  showCheckbox: boolean - Display checkbox next to options (default: false)
  showSearch: boolean - Show search/filter input in dropdown (default: false)
  options: SelectItem<T>[] - Array of option objects with structure:
    {id: string | number, value: T, label: string, disabled?: boolean}
  optgroups: SelectOptgroup<T>[] - Array of grouped options with structure:
    {label: string, options: SelectItem<T>[]}

Form Control:
  - Use with formControlName or formControl directives
  - Required state is determined by Validators.required on the form control
  - Disabled state is controlled by the form control's disabled state

Examples:
  Data-driven:
    <ui-select [options]="myOptions" label="Choose option" formControlName="field" />
    <ui-select [optgroups]="myOptgroups" label="Choose option" formControlName="field" />

  Component projection:
    <ui-select label="Choose option" formControlName="field">
      <ui-optgroup label="Group 1">
        <ui-option [value]="{id: 1, name: 'Item'}">Item Label</ui-option>
      </ui-optgroup>
    </ui-select>
-->

<div [class]="`${labelPosition()} component-wrapper`">
  <label [for]="id" class="label-text">{{ label() }}</label>
  <div class="select-container component-container">
    <button
      type="button"
      [id]="id"
      [disabled]="disabled()"
      [class.size]="size() === 'compact'"
      (click)="toggleDropdown()"
      (keydown)="handleTriggerKeydown($event)"
      (blur)="handleOnBlur()"
      [attr.aria-label]="ariaLabel()"
      [attr.aria-haspopup]="'listbox'"
      [attr.aria-expanded]="isOpen()"
      [attr.aria-controls]="dropdownId"
      [attr.aria-activedescendant]="activeDescendantId()"
      [attr.aria-required]="isRequired() || null"
      class="select-trigger component-trigger"
      #triggerButton
    >
      <span class="selected-text" aria-hidden="true">{{ displayLabel() }}</span>
      <span class="dropdown-icon" [class.open]="isOpen()" aria-hidden="true"></span>
    </button>

    @if (isOpen()) {
      <div
        [id]="dropdownId"
        class="select-dropdown component-dropdown"
        role="listbox"
        [attr.aria-labelledby]="id"
        #dropdown
      >
        @if (showSearch()) {
          <div class="select-search search-container">
            <input
              type="search"
              class="search-input"
              [placeholder]="searchPlaceholder()"
              [value]="searchTerm()"
              (input)="onSearchInput($event)"
              (click)="$event.stopPropagation()"
              aria-label="Search options"
            />
          </div>
        }
        @if (filteredOptgroups().length) {
          @for (optgroup of filteredOptgroups(); track optgroup.label) {
            <div class="optgroup" role="group" [attr.aria-label]="optgroup.label">
              <div class="optgroup-header dropdown-item">{{ optgroup.label }}</div>
              @for (option of optgroup.options; track option.id) {
                <div
                  [id]="'option-' + option.id"
                  class="option dropdown-item"
                  [class.selected]="selectedValue()?.id === option.id"
                  [class.disabled]="option.disabled"
                  (click)="selectOption(option)"
                  (keydown)="handleDropdownKeydown($event, option)"
                  role="option"
                  [attr.aria-selected]="selectedValue()?.id === option.id"
                  [attr.aria-disabled]="option.disabled"
                  [attr.tabindex]="option.disabled ? -1 : 0"
                >
                  @if (showCheckbox()) {
                    <input
                      type="checkbox"
                      [checked]="selectedValue()?.id === option.id"
                      [disabled]="option.disabled"
                      tabindex="-1"
                      aria-hidden="true"
                    />
                  }
                  {{ option.label }}
                </div>
              }
            </div>
          }
        } @else if (filteredOptions().length) {
          @for (option of filteredOptions(); track option.id) {
            <div
              [id]="'option-' + option.id"
              class="option dropdown-item"
              [class.selected]="selectedValue()?.id === option.id"
              [class.disabled]="option.disabled"
              (click)="selectOption(option)"
              (keydown)="handleDropdownKeydown($event, option)"
              role="option"
              [attr.aria-selected]="selectedValue()?.id === option.id"
              [attr.aria-disabled]="option.disabled"
              [attr.tabindex]="option.disabled ? -1 : 0"
            >
              @if (showCheckbox()) {
                <input
                  type="checkbox"
                  [checked]="selectedValue()?.id === option.id"
                  [disabled]="option.disabled"
                  tabindex="-1"
                  aria-hidden="true"
                />
              }
              {{ option.label }}
            </div>
          }
        } @else {
          <!-- needed for cases like rendering a loader -->
          <div #projectedContentWrapper>
            <ng-content></ng-content>
          </div>
          @if (!hasProjectedContent() && emptyMultiselectMessage()) {
            <div class="no-results" role="status" aria-live="polite">{{ emptyMultiselectMessage() }}</div>
          }
        }
      </div>
    }

    <div [id]="liveRegionId" class="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
      {{ liveMessage() }}
    </div>
  </div>
</div>
